import React, { useState } from "react";
import { KeyRound, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { postRequest } from "../../../api/postRequest";

/* ── Theme helpers ── */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;
  return {
    bg,
    surface2: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)",
    divider: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)",
    softShadow: isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(0,0,0,.10)",
    textPri: theme?.textPrimary ?? (isDark ? "#fff" : "#111827"),
    textSec: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
    gradient: theme?.gradientHero ?? "linear-gradient(135deg,#2563eb,#0ea5e9)",
  };
};

type ApiRes<T> = { success: boolean; errorDetail?: string; data?: T };

const InputField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  rightElement?: React.ReactNode;
  S: ReturnType<typeof useSurfaces>;
}> = ({ label, value, onChange, placeholder, type = "text", required, rightElement, S }) => (
  <label className="block">
    <span className="block text-xs mb-1" style={{ color: S.textSec }}>
      {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
    </span>
    <div
      className="flex items-center gap-2 rounded-xl px-3 py-2.5 border"
      style={{ backgroundColor: S.surface2, borderColor: S.divider }}
    >
      <KeyRound size={16} style={{ color: S.textSec }} />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent outline-none text-sm"
        style={{ color: S.textPri }}
      />
      {rightElement}
    </div>
  </label>
);

const SifrePage: React.FC = () => {
  const { theme } = useTheme();
  const { customer } = useCustomerAuth();
  const S = useSurfaces(theme);

  const [mevcutSifre, setMevcutSifre] = useState("");
  const [yeniSifre, setYeniSifre] = useState("");
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState("");
  const [showMevcut, setShowMevcut] = useState(false);
  const [showYeni, setShowYeni] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const strength = (() => {
    if (yeniSifre.length < 6) return 1;
    if (yeniSifre.length < 8) return 2;
    if (/[A-Z]/.test(yeniSifre) && /[0-9]/.test(yeniSifre)) return 4;
    return 3;
  })();

  const canSave =
    mevcutSifre.length > 0 && yeniSifre.length >= 6 && yeniSifreTekrar.length > 0 && !loading;

  const handleSave = async () => {
    if (!customer?.id) return;
    if (yeniSifre !== yeniSifreTekrar) {
      setMsg({ ok: false, text: "Yeni şifreler eşleşmiyor." });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const res = await postRequest<any, ApiRes<any>>("/MusteriAuth/sifre-degistir", {
        MusteriId: customer.id,
        MevcutSifre: mevcutSifre,
        YeniSifre: yeniSifre,
      });
      if (!res.success) throw new Error(res.errorDetail ?? "Şifre değiştirme başarısız.");
      setMsg({ ok: true, text: "Şifreniz başarıyla değiştirildi." });
      setMevcutSifre("");
      setYeniSifre("");
      setYeniSifreTekrar("");
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message ?? "Bir hata oluştu." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-sm">
      <div>
        <h2 className="text-base font-semibold" style={{ color: S.textPri }}>
          Şifre Değiştir
        </h2>
        <p className="text-xs mt-0.5" style={{ color: S.textSec }}>
          Güvenliğiniz için şifrenizi düzenli aralıklarla güncelleyin.
        </p>
      </div>

      <InputField
        label="Mevcut Şifre"
        value={mevcutSifre}
        onChange={setMevcutSifre}
        placeholder="••••••••"
        type={showMevcut ? "text" : "password"}
        required
        S={S}
        rightElement={
          <button type="button" onClick={() => setShowMevcut((v) => !v)} style={{ color: S.textSec }}>
            {showMevcut ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
      />

      <InputField
        label="Yeni Şifre"
        value={yeniSifre}
        onChange={setYeniSifre}
        placeholder="En az 6 karakter"
        type={showYeni ? "text" : "password"}
        required
        S={S}
        rightElement={
          <button type="button" onClick={() => setShowYeni((v) => !v)} style={{ color: S.textSec }}>
            {showYeni ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
      />

      {/* Güç göstergesi */}
      {yeniSifre.length > 0 && (
        <div className="space-y-1">
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((lvl) => (
              <div
                key={lvl}
                className="h-1 flex-1 rounded-full transition-colors"
                style={{
                  backgroundColor:
                    lvl <= strength
                      ? strength <= 1 ? "#ef4444"
                        : strength <= 2 ? "#f59e0b"
                        : strength <= 3 ? "#22c55e"
                        : "#10b981"
                      : S.surface2,
                }}
              />
            ))}
          </div>
          <p className="text-xs" style={{ color: S.textSec }}>
            {strength <= 1 ? "Çok kısa" : strength <= 2 ? "Zayıf" : strength <= 3 ? "Orta" : "Güçlü"}
          </p>
        </div>
      )}

      <InputField
        label="Yeni Şifre Tekrar"
        value={yeniSifreTekrar}
        onChange={setYeniSifreTekrar}
        placeholder="Şifreyi tekrar girin"
        type="password"
        required
        S={S}
      />

      {yeniSifreTekrar.length > 0 && yeniSifre !== yeniSifreTekrar && (
        <p className="text-xs" style={{ color: "#ef4444" }}>Şifreler eşleşmiyor.</p>
      )}

      {msg && (
        <div
          className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm border"
          style={{
            backgroundColor: msg.ok ? "rgba(16,185,129,.12)" : "rgba(239,68,68,.10)",
            borderColor: msg.ok ? "rgba(16,185,129,.3)" : "rgba(239,68,68,.3)",
            color: msg.ok ? "#047857" : "#b91c1c",
          }}
        >
          {msg.ok && <CheckCircle2 size={16} />}
          {msg.text}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={!canSave}
        className="inline-flex items-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition"
        style={{ background: S.gradient }}
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
        {loading ? "Değiştiriliyor…" : "Şifreyi Değiştir"}
      </button>
    </div>
  );
};

export default SifrePage;
